
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import style from "./Offers.module.css";
import { HiOutlineArrowLeft, HiDownload, HiOutlineOfficeBuilding, HiCurrencyRupee, HiOutlineCalendar, HiChevronRight, HiCheckCircle, HiXCircle } from "react-icons/hi";
import { MdMoreVert } from "react-icons/md";
import { BiSearch } from "react-icons/bi";
import { baseurl } from '../Url';
import Loader from "../Loder/Loader";

const STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "Pending", label: "Pending" },
    { value: "Offer Accepted", label: "Offer Accepted" },
    { value: "Offer Rejected", label: "Offer Rejected" },
    { value: "Withdrawn", label: "Withdrawn" },
];

const SORT_OPTIONS = [
    { value: "High Relevance", label: "High Relevance" },
    { value: "Recently Published", label: "Recently Published" },
    { value: "Validity", label: "Validity" },
];

const Offers = () => {
    const navigate = useNavigate();
    const { onMenuToggle, sidebarOpen } = useOutletContext() || {};
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [viewingOffer, setViewingOffer] = useState(null);
    const [newComment, setNewComment] = useState("");
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, type: null, offer: null });
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [sortBy, setSortBy] = useState("High Relevance");
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [sortMenuOpen, setSortMenuOpen] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [statusUpdateMessage, setStatusUpdateMessage] = useState("");
    const statusMenuRef = useRef(null);
    const sortMenuRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchMyOffers = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setIsLoading(true);
        }
        setErrorMessage("");

        const token = JSON.parse(localStorage.getItem("access-token"));
        if (!token) {
            setErrorMessage("Please login to view offers.");
            if (!silent) {
                setIsLoading(false);
            }
            return;
        }

        try {
            const params = new URLSearchParams();
            if (debouncedSearch) params.set("search", debouncedSearch);
            if (filterStatus) params.set("status", filterStatus);
            if (sortBy) params.set("sort_by", sortBy);
            const qs = params.toString();
            const url = qs ? `${baseurl}/offers/my-list?${qs}` : `${baseurl}/offers/my-list`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            const data = await response.json();

            if (data.status === "success") {
                setOffers(data.data || []);
            } else {
                setErrorMessage(data.message || "Failed to load offers.");
            }
        } catch (error) {
            console.error("Error fetching offers:", error);
            setErrorMessage("An error occurred while loading offers.");
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, [debouncedSearch, filterStatus, sortBy]);

    useEffect(() => {
        fetchMyOffers();
    }, [fetchMyOffers]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
                setStatusMenuOpen(false);
            }
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
                setSortMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("touchstart", onClickOutside);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("touchstart", onClickOutside);
        };
    }, []);

    const getStatusColor = (status) => {
        if (!status) return "#8C8C8C";
        switch (status.toLowerCase()) {
            case "offer accepted":
            case "active":
                return "#00DFC7";
            case "pending":
                return "#FFBE00";
            case "withdrawn":
            case "rejected":
                return "#FF4D4F";
            default:
                return "#595BDB";
        }
    };

    const getStatusBg = (status) => {
        if (!status) return "rgba(140,140,140,0.1)";
        switch (status.toLowerCase()) {
            case "offer accepted":
            case "active":
                return "rgba(0,223,199,0.1)";
            case "pending":
                return "rgba(255,190,0,0.1)";
            case "withdrawn":
            case "rejected":
                return "rgba(255,77,79,0.1)";
            default:
                return "rgba(89,91,219,0.1)";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === "-") return "-";
        const date = new Date(dateString);
        if (isNaN(date)) return dateString;
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const handleView = (offer) => {
        setViewingOffer(offer);
    };

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !viewingOffer) return;

        setIsSubmittingComment(true);
        const token = JSON.parse(localStorage.getItem("access-token"));

        try {
            const response = await fetch(`${baseurl}/offers/${viewingOffer.id}/comment`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: newComment,
                    sender: "Candidate"
                }),
            });

            const data = await response.json();
            if (data.status === "success") {
                setNewComment("");
                // Refresh viewing offer to show new comment
                const updatedOfferRes = await fetch(`${baseurl}/offers/${viewingOffer.id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                });
                const updatedOfferData = await updatedOfferRes.json();
                if (updatedOfferData.status === "success") {
                    setViewingOffer(updatedOfferData.data);
                }
            } else {
                alert(data.message || "Failed to submit comment.");
            }
        } catch (error) {
            console.error("Error submitting comment:", error);
            alert("An error occurred while submitting your comment.");
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const updateOfferStatus = async (offerId, newStatus) => {
        if (isUpdatingStatus) return false;
        const token = JSON.parse(localStorage.getItem("access-token"));
        if (!token) {
            setErrorMessage("Please login to update offers.");
            return false;
        }

        setIsUpdatingStatus(true);
        setStatusUpdateMessage("");

        setOffers((prev) => prev.map((offer) => (
            offer.id === offerId ? { ...offer, status: newStatus } : offer
        )));
        setViewingOffer((prev) => (
            prev && prev.id === offerId ? { ...prev, status: newStatus } : prev
        ));

        try {
            const response = await fetch(`${baseurl}/offers/${offerId}/status`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus }),
            });

            const data = await response.json();
            if (data.status === "success") {
                setStatusUpdateMessage(`${newStatus} successfully. Redirecting to Offers...`);
                await fetchMyOffers({ silent: true });
                setTimeout(() => {
                    setViewingOffer(null);
                    setStatusUpdateMessage("");
                    navigate("/offers", { replace: true });
                }, 900);
                return true;
            } else {
                setStatusUpdateMessage("");
                await fetchMyOffers({ silent: true });
                alert(data.message || `Failed to update status to ${newStatus}`);
            }
        } catch (error) {
            console.error("Error updating offer status:", error);
            setStatusUpdateMessage("");
            await fetchMyOffers({ silent: true });
            alert("An error occurred while updating the offer status.");
        } finally {
            setIsUpdatingStatus(false);
        }
        return false;
    };

    const handleAccept = (offer) => {
        setConfirmModal({ show: true, type: 'accept', offer });
    };

    const handleReject = (offer) => {
        setConfirmModal({ show: true, type: 'reject', offer });
    };

    const handleConfirmAction = async () => {
        const selectedOffer = confirmModal.offer;
        if (!selectedOffer?.id) {
            setConfirmModal({ show: false, type: null, offer: null });
            return;
        }

        const targetStatus = confirmModal.type === 'accept'
            ? "Offer Accepted"
            : confirmModal.type === 'reject'
                ? "Offer Rejected"
                : null;

        setConfirmModal({ show: false, type: null, offer: null });
        if (!targetStatus) return;

        await updateOfferStatus(selectedOffer.id, targetStatus);
    };

    const handleCancelAction = () => {
        setConfirmModal({ show: false, type: null, offer: null });
    };

    return (
        <div className={style.container}>
            <div className={style.contentSection}>

                {viewingOffer ? (
                    /* ─── DETAIL VIEW ─── */
                    <div className={style.detailViewContainer}>
                        <div className={style.detailHeader}>
                            {typeof onMenuToggle === "function" && !sidebarOpen && (
                                <button
                                    type="button"
                                    className={style.mobileHeaderMenuBtn}
                                    onClick={onMenuToggle}
                                    aria-label="Open menu"
                                >
                                    <MdMoreVert size={20} />
                                </button>
                            )}
                            <button className={style.backBtn} onClick={() => setViewingOffer(null)}>
                                <HiOutlineArrowLeft size={20} />
                            </button>
                            <div className={style.detailTitle}>
                                <h2>{viewingOffer.job_title}</h2>
                                <p>{viewingOffer.employer_name || "Company"}</p>
                            </div>
                        </div>

                        {/* Offer Letter Download */}
                        {viewingOffer.offer_letter && viewingOffer.offer_letter.url && (
                            <div className={style.heroDocument}>
                                <div className={style.heroDocInfo}>
                                    <h4>Offer Letter</h4>
                                    <p>{viewingOffer.offer_letter.name || "Official Offer Document"}</p>
                                </div>
                                <a
                                    href={viewingOffer.offer_letter.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={style.docDownloadBtn}
                                >
                                    <HiDownload size={18} /> Download PDF
                                </a>
                            </div>
                        )}

                        <div className={style.detailGridContainer}>
                            {/* Left: Job Info + Compensation + Dates */}
                            <div>
                                <div className={style.infoSection}>
                                    <h3 className={style.sectionTitle}>
                                        <span className={style.sectionIcon}><HiOutlineOfficeBuilding /></span>
                                        Job Information
                                    </h3>
                                    <div className={style.gridContent}>
                                        <div className={style.gridItem}>
                                            <label>Job Title</label>
                                            <span>{viewingOffer.job_title}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Designation</label>
                                            <span>{viewingOffer.designation || "-"}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Work Mode</label>
                                            <span>{viewingOffer.work_mode || "-"}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Work Location</label>
                                            <span>{viewingOffer.work_location || "-"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={style.infoSection}>
                                    <h3 className={style.sectionTitle}>
                                        <span className={style.sectionIcon}><HiCurrencyRupee /></span>
                                        Compensation
                                    </h3>
                                    <div className={style.gridContent}>
                                        <div className={style.gridItem}>
                                            <label>Annual CTC</label>
                                            <span>{viewingOffer.ctc_annual ? `₹${viewingOffer.ctc_annual.toLocaleString("en-IN")}` : "-"}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Joining Bonus</label>
                                            <span>{viewingOffer.joining_bonus || "-"}</span>
                                        </div>
                                        <div className={`${style.gridItem} ${style.gridItemFull}`}>
                                            <label>Salary Breakdown</label>
                                            <span>{viewingOffer.salary_breakdown || "Not specified"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={style.infoSection}>
                                    <h3 className={style.sectionTitle}>
                                        <span className={style.sectionIcon}><HiOutlineCalendar /></span>
                                        Important Dates
                                    </h3>
                                    <div className={style.gridContent}>
                                        <div className={style.gridItem}>
                                            <label>Joining Date</label>
                                            <span>{viewingOffer.offered_joining_date || viewingOffer.join_date || "-"}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Notice Period</label>
                                            <span>{viewingOffer.notice_period_days ? `${viewingOffer.notice_period_days} Days` : "-"}</span>
                                        </div>
                                        <div className={style.gridItem}>
                                            <label>Valid Until</label>
                                            <span>{viewingOffer.offer_valid_until || "-"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Actions + Comments */}
                            <div className={style.interactionBar}>
                                <div className={style.currentStatusRow}>
                                    <span className={style.currentStatusLabel}>Current Status</span>
                                    <span
                                        className={style.statusBadge}
                                        style={{
                                            color: getStatusColor(viewingOffer.status),
                                            background: getStatusBg(viewingOffer.status)
                                        }}
                                    >
                                        {viewingOffer.status || "Pending"}
                                    </span>
                                </div>

                                {statusUpdateMessage ? (
                                    <p className={style.statusUpdateNotice}>{statusUpdateMessage}</p>
                                ) : null}

                                {viewingOffer.status !== "Offer Accepted" && viewingOffer.status !== "Offer Rejected" && (
                                    <div className={style.actionPanel}>
                                        <button className={style.primaryBtn} onClick={() => handleAccept(viewingOffer)} disabled={isUpdatingStatus}>
                                            {isUpdatingStatus ? "Updating..." : "Accept Offer"}
                                        </button>
                                        <button className={style.secondaryBtn} onClick={() => handleReject(viewingOffer)} disabled={isUpdatingStatus}>
                                            {isUpdatingStatus ? "Updating..." : "Decline Offer"}
                                        </button>
                                    </div>
                                )}

                                <div className={style.discussionArea}>
                                    <h4 style={{ margin: '0 0 1.5rem 0', fontSize: '1rem', color: '#1C1C1E' }}>Discussion</h4>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1rem' }}>
                                        {viewingOffer.comments && viewingOffer.comments.length > 0 ? (
                                            viewingOffer.comments.map((comment, index) => (
                                                <div
                                                    key={index}
                                                    className={`${style.commentBubble} ${comment.sender === "Candidate" ? style.candidate : ""}`}
                                                >
                                                    <div className={style.commentHeader}>
                                                        <span className={style.commentAuthor}>{comment.sender}</span>
                                                        <span className={style.commentTime}>
                                                            {new Date(comment.timestamp).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className={style.commentText}>{comment.message}</p>
                                                </div>
                                            ))
                                        ) : (
                                            <p style={{ color: '#8E8E93', fontSize: '0.9rem', fontStyle: 'italic', textAlign: 'center' }}>No messages yet.</p>
                                        )}
                                    </div>
                                    <form className={style.commentInput} onSubmit={handleSubmitComment}>
                                        <textarea
                                            placeholder="Write a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            disabled={isSubmittingComment}
                                        />
                                        <button
                                            type="submit"
                                            className={style.sendCommentBtn}
                                            disabled={isSubmittingComment || !newComment.trim()}
                                        >
                                            {isSubmittingComment ? "Sending..." : "Send"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ─── LIST VIEW ─── */
                    <>
                        <div className={style.listToolbar}>
                            {typeof onMenuToggle === "function" && !sidebarOpen && (
                                <button
                                    type="button"
                                    className={style.mobileHeaderMenuBtn}
                                    onClick={onMenuToggle}
                                    aria-label="Open menu"
                                >
                                    <MdMoreVert size={20} />
                                </button>
                            )}
                            <div className={style.searchField}>
                                <BiSearch size={20} style={{ color: "var(--text-muted)", flexShrink: 0 }} aria-hidden />
                                <input
                                    type="search"
                                    placeholder="Search by job title, role, location…"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    aria-label="Search offers"
                                />
                            </div>
                            <div className={style.statusCustomWrapper} ref={statusMenuRef}>
                                <button
                                    type="button"
                                    className={style.filterSelect}
                                    onClick={() => {
                                        setStatusMenuOpen((prev) => !prev);
                                        setSortMenuOpen(false);
                                    }}
                                    aria-expanded={statusMenuOpen}
                                    aria-label="Filter by status"
                                >
                                    {STATUS_OPTIONS.find((opt) => opt.value === filterStatus)?.label || "All statuses"}
                                </button>
                                {statusMenuOpen && (
                                    <div className={style.statusCustomMenu}>
                                        {STATUS_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value || "all-statuses"}
                                                type="button"
                                                className={`${style.statusCustomOption} ${filterStatus === opt.value ? style.statusCustomOptionActive : ""}`}
                                                onClick={() => {
                                                    setFilterStatus(opt.value);
                                                    setStatusMenuOpen(false);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={style.sortCustomWrapper} ref={sortMenuRef}>
                                <button
                                    type="button"
                                    className={style.filterSelect}
                                    onClick={() => setSortMenuOpen((prev) => !prev)}
                                    aria-expanded={sortMenuOpen}
                                    aria-label="Sort offers"
                                >
                                    {SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label || "High Relevance"}
                                </button>
                                {sortMenuOpen && (
                                    <div className={style.sortCustomMenu}>
                                        {SORT_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`${style.sortCustomOption} ${sortBy === opt.value ? style.sortCustomOptionActive : ""}`}
                                                onClick={() => {
                                                    setSortBy(opt.value);
                                                    setSortMenuOpen(false);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {isLoading ? (
                            <Loader />
                        ) : errorMessage ? (
                            <p style={{ color: "red" }}>{errorMessage}</p>
                        ) : (
                        <div className={style.offersGrid}>
                            {offers.length > 0 ? (
                                offers.map((offer, index) => (
                                    <div
                                        key={offer.id || index}
                                        className={style.offerCard}
                                        style={{ animationDelay: `${index * 0.08}s` }}
                                    >
                                        <div className={style.cardHeader}>
                                            <div className={style.companyIcon}>
                                                <HiOutlineOfficeBuilding />
                                            </div>
                                            <span
                                                className={style.statusBadge}
                                                style={{
                                                    color: getStatusColor(offer.status),
                                                    background: getStatusBg(offer.status)
                                                }}
                                            >
                                                {offer.status}
                                            </span>
                                        </div>

                                        <div className={style.cardBody}>
                                            <h3>{offer.job_title}</h3>
                                            <p className={style.companyName}>
                                                <HiOutlineOfficeBuilding />
                                                {offer.employer_name || "Company"}
                                            </p>
                                        </div>

                                        <div className={style.cardMetrics}>
                                            <div className={style.metric}>
                                                <span className={style.metricLabel}>Annual CTC</span>
                                                <span className={style.metricValue}>
                                                    {offer.ctc_annual ? `₹${offer.ctc_annual.toLocaleString("en-IN")}` : "-"}
                                                </span>
                                            </div>
                                            <div className={style.metric}>
                                                <span className={style.metricLabel}>Joining Date</span>
                                                <span className={style.metricValue}>
                                                    {formatDate(offer.offered_joining_date || offer.join_date)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={style.cardFooter}>
                                            <button
                                                className={style.viewDetailsBtn}
                                                onClick={() => handleView(offer)}
                                            >
                                                View Details <HiChevronRight />
                                            </button>

                                            {offer.status !== "Offer Accepted" && offer.status !== "Offer Rejected" && (
                                                <div className={style.cardActions}>
                                                    <button
                                                        className={`${style.actionIconBtn} accept`}
                                                        onClick={() => handleAccept(offer)}
                                                        title="Accept Offer"
                                                        disabled={isUpdatingStatus}
                                                    >
                                                        <HiCheckCircle size={20} />
                                                    </button>
                                                    <button
                                                        className={`${style.actionIconBtn} reject`}
                                                        onClick={() => handleReject(offer)}
                                                        title="Reject Offer"
                                                        disabled={isUpdatingStatus}
                                                    >
                                                        <HiXCircle size={20} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '5rem', background: 'white', borderRadius: '28px', border: '1px dashed #e0e0e0' }}>
                                    <HiOutlineOfficeBuilding size={48} style={{ color: '#E5E5EA', marginBottom: '1rem' }} />
                                    <p style={{ color: '#8E8E93', fontSize: '1.1rem', margin: 0 }}>No offers found yet.</p>
                                </div>
                            )}
                        </div>
                        )}
                    </>
                )}
            </div>

            {/* Confirmation Modal */}
            {confirmModal.show && (
                <>
                    <div className={style.popupOverlay}></div>
                    <div className={style.Popup}>
                        <div className={style.popupText}>
                            <p>
                                Are you sure you want to {confirmModal.type} this offer?
                            </p>
                        </div>
                        <div className={style.PopupButton}>
                            <button
                                className={style.noButton}
                                onClick={handleCancelAction}
                                disabled={isUpdatingStatus}
                            >
                                NO
                            </button>
                            <button
                                className={style.yesButton}
                                onClick={handleConfirmAction}
                                disabled={isUpdatingStatus}
                            >
                                {isUpdatingStatus ? "UPDATING..." : "YES"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Offers;
